import React, { useState, useMemo } from 'react';
import { Flex, TextV2, ButtonV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { MapMarker } from '@withjoy/joykit/icons';
import { GuestSiteComponentProps } from '@apps/websiteCustomJsonComponents/types';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { OverrideTypography } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.types';
import { Category } from '@graphql/generated';
import { useScaleGuestSiteFontSize } from '@apps/guest/components/GuestSiteTypographyOverride/hooks/useScaleGuestSiteFontSize';
import wrapHtmlWithSafeLink from '@shared/components/SafeLink/wrapHtmlWithSafeLink';
import { withWindow } from '@shared/utils/withWindow';
import { PlaceToStayComponent } from './PlaceToStayComponent.types';
import { useHotelRoutePaths } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useTranslation } from '@shared/core';
import { useCustomPageAccommodationData } from './hooks/useCustomPageAccommodationData';
import { PlaceToStayComponentSkeleton } from './components/PlaceToStayComponentSkeleton';
import { LinkConfirmationDialog } from '@shared/components/SafeLink/LinkConfirmationDialog';
import { ensureHttpsPrefix } from '@shared/utils/urlHelpers';
import { usePlaceToStayComponentTelemetry } from './PlaceToStayComponent.telemetry';
import { useEventPlaces } from '@shared/utils/eventPlace';
import { distanceInMiles } from '@shared/utils/distanceInMiles';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';

const CONTAINER_BASE_WIDTH_PX = 590;
const SPACING_WITH_NOTE = pxToRem(40);
const SPACING_WITHOUT_NOTE = pxToRem(16);
const BUTTON_MARGIN_TOP_WITH_NOTE = pxToRem(8);

const getSpacing = (hasNote: boolean) => ({
  rowGap: hasNote ? SPACING_WITH_NOTE : SPACING_WITHOUT_NOTE,
  buttonMarginTop: hasNote ? BUTTON_MARGIN_TOP_WITH_NOTE : 0,
});

const NoteWithSafeLinkBase = ({ id, text }: Readonly<{ id: string; text: string }>) => {
  return (
    <TextV2
      width="100%"
      className={`place-to-stay-note-${id}`}
      typographyVariant="body2"
      wordBreak="break-word"
      dangerouslySetInnerHTML={{ __html: text }}
    />
  );
};

const NoteWithSafeLink = wrapHtmlWithSafeLink(NoteWithSafeLinkBase);

const typographyOverride: OverrideTypography = [
  { variant: 'hed5', category: Category.HEADING },
  { variant: 'body2', category: Category.PARAGRAPH },
];

export const PlaceToStayComponentGuest = (props: GuestSiteComponentProps) => {
  const data = props.data.content as PlaceToStayComponent;
  const { scaleFontSize } = useScaleGuestSiteFontSize();
  const { tq } = useTranslation('placeToStayComponent');
  const hotelRoute = useHotelRoutePaths();
  const { eventHandle, eventInfo } = useEventInfo();
  const { venueInfo } = useEventPlaces(false);
  const telemetry = usePlaceToStayComponentTelemetry();
  const { currentPageSlug } = useCurrentPageSlug(eventHandle || '', false);
  const { customPageAccommodationsMap, loading } = useCustomPageAccommodationData();
  const accommodation = data.accommodationPlaceId ? (customPageAccommodationsMap.get(data.accommodationPlaceId) ?? null) : null;
  const [showSafeLinkDialog, setShowSafeLinkDialog] = useState(false);
  const [linkToOpen, setLinkToOpen] = useState<string | null>(null);

  // Prepare telemetry data
  const telemetryData = useMemo(
    () => ({
      location: {
        lat: venueInfo?.latitude || eventInfo?.locationInfo?.latitude,
        lng: venueInfo?.longitude || eventInfo?.locationInfo?.longitude,
      },
      venueName: venueInfo?.name ?? eventInfo?.locationInfo?.name ?? '',
    }),
    [venueInfo, eventInfo],
  );

  // Generate redirect URL using the accommodation place route with UTM params
  const redirectUrl =
    eventHandle && accommodation?.id
      ? hotelRoute.accommodationPlace.buildRelativeUrl(eventHandle, accommodation.id, {
          utm_medium: 'web',
          utm_source: 'website_custom_page',
          utm_campaign: 'place_to_stay_component',
        })
      : null;

  // Use custom URL if provided, otherwise use generated redirect URL or accommodation URL
  const link = accommodation?.customUrl || redirectUrl || null;

  const handleViewHotel = useEventCallback(() => {
    if (!link) return;

    // If custom URL, show safe link dialog
    if (accommodation?.customUrl) {
      setLinkToOpen(ensureHttpsPrefix(accommodation.customUrl));
      setShowSafeLinkDialog(true);
    } else if (redirectUrl) {
      // Only trigger telemetry when redirectUrl exists (internal navigation)
      const distance =
        accommodation?.lat && accommodation?.long && telemetryData.location.lat && telemetryData.location.lng
          ? distanceInMiles({ latitude: accommodation.lat, longitude: accommodation.long }, telemetryData.location)
          : undefined;

      const hotel = {
        eventHandle: eventHandle || '',
        accommodationPlaceId: accommodation?.id || '',
        hotelName: accommodation?.customName || accommodation?.name || '',
        venueName: telemetryData.venueName,
        customPage: currentPageSlug || undefined,
        ...(distance && { milesFromVenue: distance.toFixed(1) }),
      };

      const hotelInfo = {
        hotelName: accommodation?.customName || accommodation?.name || '',
        accommodationsType: accommodation?.type || undefined,
        from: 'placeToStayComponent',
        customPage: currentPageSlug || undefined,
      };

      telemetry.viewHotelPDPPage(hotel, undefined, undefined, currentPageSlug);
      telemetry.accommodationsLinkInteracted(redirectUrl, hotelInfo, 'placeToStayComponent', currentPageSlug);
      withWindow(global => {
        global.location.href = redirectUrl;
      });
    }
  }, [link, accommodation?.customUrl, redirectUrl, accommodation, eventHandle, telemetry, currentPageSlug, telemetryData]);

  const handleCloseSafeLinkDialog = useEventCallback(() => {
    setShowSafeLinkDialog(false);
    setLinkToOpen(null);
  }, []);

  const handleAcceptSafeLink = useEventCallback(() => {
    if (linkToOpen) {
      withWindow(global => {
        global.open(linkToOpen, '_blank');
      });
    }
    handleCloseSafeLinkDialog();
  }, [linkToOpen, handleCloseSafeLinkDialog]);

  if (loading) {
    return <PlaceToStayComponentSkeleton />;
  }

  if (!accommodation) {
    return null;
  }
  const name = accommodation.customName || accommodation.name || '';
  const address = accommodation.customAddress || accommodation.address || '';
  const note = accommodation.userNote || '';

  // Determine if we have a link to show
  const hasLink = !!link;
  const spacing = getSpacing(!!note);

  return (
    <GuestSiteTypographyOverride override={typographyOverride}>
      <Flex
        textAlign="left"
        width={{ _: '100%', sm2: CONTAINER_BASE_WIDTH_PX }}
        maxWidth={pxToRem(590)}
        rowGap={spacing.rowGap}
        margin="auto"
        marginBottom={pxToRem(40)}
        flexDirection="column"
      >
        {/* Hotel Name */}
        <Flex
          flexDirection="column"
          rowGap={5}
        >
          {name && (
            <TextV2
              typographyVariant="hed5"
              textAlign="center"
              lineHeight={1.2}
            >
              {name}
            </TextV2>
          )}
          {/* Address with Location Icon */}
          {address && (
            <Flex
              alignItems="center"
              justifyContent="center"
              columnGap={pxToRem(8)}
              flexDirection="row"
            >
              <MapMarker
                size={16}
                color="currentColor"
              />
              <TextV2
                typographyVariant="body2"
                textAlign="center"
                fontSize={scaleFontSize(pxToRem(14), Category.PARAGRAPH)}
                wordBreak="break-word"
              >
                {address}
              </TextV2>
            </Flex>
          )}
        </Flex>

        {/* Note/Description */}
        {note && (
          <Flex
            width="100%"
            flexDirection="column"
            rowGap={pxToRem(16)}
            textAlign="center"
          >
            <NoteWithSafeLink
              id={accommodation.id}
              text={note}
            />
          </Flex>
        )}

        {/* View Hotel Button */}
        {hasLink && (
          <Flex
            justifyContent="center"
            marginTop={spacing.buttonMarginTop}
            width="100%"
          >
            <ButtonV2
              variant="outline"
              border="1px solid"
              borderColor="currentColor"
              shape="rounded"
              width={{ _: '100%', sm2: pxToRem(292) }}
              paddingX={pxToRem(24)}
              paddingY={pxToRem(12)}
              onClick={handleViewHotel}
            >
              <TextV2 typographyVariant="button2">{tq`View Hotel`}</TextV2>
            </ButtonV2>
          </Flex>
        )}
      </Flex>
      {linkToOpen && (
        <LinkConfirmationDialog
          href={linkToOpen}
          isOpen={showSafeLinkDialog}
          onAccept={handleAcceptSafeLink}
          onClose={handleCloseSafeLinkDialog}
          target="_blank"
        />
      )}
    </GuestSiteTypographyOverride>
  );
};
